import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Container,
  Grid,
  Fade,
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  PointOfSale,
} from '@mui/icons-material'
import { useStore } from '../store/authStore'
import '../styles/global.css'

export default function LoginPage() {
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const { login, isLoading, error, clearError } = useStore()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()

    if (!username.trim() || !password.trim()) {
      return
    }

    try {
      await login(username, password)
      navigate('/dashboard')
    } catch (error) {
      // الخطأ معالج في الـ store
    }
  }

  return (
    <Fade in={true} timeout={800}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* خلفية زخرفية */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100%',
            height: '100%',
            background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          }}
        />
        
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center" justifyContent="center">
            {/* قسم الشعار والمعلومات */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  textAlign: 'center',
                  color: 'white',
                  p: 4,
                }}
              >
                <PointOfSale sx={{ fontSize: 80, mb: 3, opacity: 0.9 }} />
                <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                  نظام تفعيل وصيانة أجهزة POS
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, mb: 4, lineHeight: 1.8 }}>
                  نظام متكامل لإدارة تفعيل الأجهزة وتتبع زيارات الصيانة للعملاء
                </Typography>
              </Box>
            </Grid>

            {/* قسم تسجيل الدخول */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={10}
                sx={{
                  p: { xs: 3, md: 5 },
                  width: '100%',
                  maxWidth: 450,
                  borderRadius: 4,
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700, color: '#1976d2' }}>
                    تسجيل الدخول
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    أدخل بيانات الدخول للوصول إلى لوحة التحكم
                  </Typography>
                </Box>

                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 3,
                      borderRadius: 2,
                      border: '1px solid #f44336',
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="اسم المستخدم"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    margin="normal"
                    required
                    disabled={isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person sx={{ color: '#1976d2' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'rgba(25, 118, 210, 0.05)',
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    label="كلمة المرور"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    margin="normal"
                    required
                    disabled={isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: '#1976d2' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: '#1976d2' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'rgba(25, 118, 210, 0.05)',
                      },
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    sx={{ 
                      mt: 4, 
                      mb: 3, 
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #1976d2, #1565c0)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1565c0, #1976d2)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(25, 118, 210, 0.3)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'تسجيل الدخول'
                    )}
                  </Button>

                  <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      نظام إدارة تفعيل وصيانة أجهزة POS
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      الإصدار 1.0.0 | © 2024 جميع الحقوق محفوظة
                    </Typography>
                  </Box>
                </form>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Fade>
  )
}
